import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  EventAdminView,
  EventRequest,
  EventWinnerRequest,
  EventAnnounceRequest,
  EventAnnounceResponse,
  CmsPage,
} from '../types/cms';

const BASE = '/admin/v1/events';

/**
 * [event-form-fix] BE 검증 400 의 per-field details 를 운영자 친화 메시지로 변환한다.
 * 종전엔 `create failed: 400` 으로 envelope 를 버려, 필수값 누락(시작일/종료일/썸네일
 * — EventCampaignRequest @NotNull/@NotBlank) 시 원인 없는 실패로 보였고, 사용자는
 * "저장해도 탭이 계속 비활성"을 수정 페이지 고장으로 인지했다(라이브 재현).
 */
const EVENT_FIELD_LABELS: Record<string, string> = {
  title: '제목',
  thumbnailUrl: '썸네일',
  startDate: '시작일',
  endDate: '종료일',
  eventKey: '이벤트 키',
  status: '상태',
};

const throwEventRequestError = async (res: Response, fallback: string): Promise<never> => {
  let detail = '';
  try {
    const env = (await res.json()) as {
      error?: { message?: string; details?: Record<string, string> } | null;
    };
    const details = env.error?.details
      ? Object.entries(env.error.details)
          .map(([field, msg]) => `${EVENT_FIELD_LABELS[field] ?? field}: ${msg}`)
          .join(' · ')
      : '';
    detail = details || env.error?.message || '';
  } catch {
    // non-JSON(게이트웨이 에러 등) — 상태코드 폴백.
  }
  throw new Error(detail ? `${fallback} — ${detail}` : `${fallback} (${res.status})`);
};

const fetchEvents = async (page: number, size: number): Promise<CmsPage<EventAdminView>> => {
  const q = new URLSearchParams({ page: String(page), size: String(size), sort: 'sortOrder,asc' });
  const res = await adminFetch(`${BASE}?${q}`);
  if (!res.ok) throw new Error(`events list failed: ${res.status}`);
  const env = (await res.json()) as { data?: EventAdminView[] | CmsPage<EventAdminView> };
  const raw = env.data;
  if (Array.isArray(raw)) return { content: raw, totalElements: raw.length, totalPages: 1, number: 0, size };
  return raw ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
};

const fetchEvent = async (eventId: string): Promise<EventAdminView> => {
  const res = await adminFetch(`${BASE}/${eventId}`);
  if (!res.ok) throw new Error(`event fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: EventAdminView };
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const eventsQueryKey = (page: number, size: number) =>
  ['admin', 'events', page, size] as const;

export const eventQueryKey = (eventId: string) =>
  ['admin', 'event', eventId] as const;

export const useEventsQuery = (page = 0, size = 20) =>
  useQuery({ queryKey: eventsQueryKey(page, size), queryFn: () => fetchEvents(page, size) });

/**
 * QA-356 — 이벤트 관리 검색용 전체 목록. BE 목록(GET /admin/v1/events)이 검색 조건을 받지 않아 전부 받아 와
 * 화면에서 거른다(이벤트 수가 많지 않다 — 500건씩). BE 검색 파라미터가 생기면 서버 검색으로 바꾼다.
 */
export const fetchAllEvents = async (): Promise<EventAdminView[]> => {
  const SIZE = 500;
  const first = await fetchEvents(0, SIZE);
  const all = [...first.content];
  for (let p = 1; p < (first.totalPages ?? 1); p++) all.push(...(await fetchEvents(p, SIZE)).content);
  return all;
};

export const useAllEventsQuery = (enabled = true) =>
  useQuery({ queryKey: ['admin', 'events', 'all'] as const, queryFn: fetchAllEvents, enabled });

export const useEventQuery = (eventId: string) =>
  useQuery({ queryKey: eventQueryKey(eventId), queryFn: () => fetchEvent(eventId), enabled: !!eventId && eventId !== 'new' });

export const useEventMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'events'] });

  const create = useMutation({
    mutationFn: async (body: EventRequest) => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) await throwEventRequestError(res, '이벤트 등록에 실패했습니다');
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ eventId, body }: { eventId: string; body: EventRequest }) => {
      const path = `${BASE}/${eventId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) await throwEventRequestError(res, '이벤트 저장에 실패했습니다');
    },
    // 수정 후 다시 열면 이전 값이 보이던 문제 — 목록만 무효화하면 단건 캐시(staleTime 30초)가 그대로 폼에 들어간다.
    onSuccess: (_, { eventId }) => {
      void qc.invalidateQueries({ queryKey: eventQueryKey(eventId) });
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (eventId: string) => {
      const path = `${BASE}/${eventId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const announceWinners = useMutation({
    mutationFn: async ({ eventId, body }: { eventId: string; body: EventWinnerRequest }) => {
      const path = `${BASE}/${eventId}/winners`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`announce winners failed: ${res.status}`);
    },
    onSuccess: (_, { eventId }) => {
      void qc.invalidateQueries({ queryKey: eventQueryKey(eventId) });
      void invalidate();
    },
  });

  const announce = useMutation({
    mutationFn: async ({ eventId, body }: { eventId: string; body: EventAnnounceRequest }): Promise<EventAnnounceResponse> => {
      const path = `${BASE}/${eventId}/announce`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`announce failed: ${res.status}`);
      const env = (await res.json()) as { data?: EventAnnounceResponse };
      if (!env.data) throw new Error('no announce response data');
      return env.data;
    },
    onSuccess: (_, { eventId }) => {
      void qc.invalidateQueries({ queryKey: eventQueryKey(eventId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'event-applications', eventId] });
      void invalidate();
    },
  });

  return { create, update, remove, announceWinners, announce };
};
